import React, { useEffect, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../../components/Header';
import Container from '../../components/Container';
import { api } from '../../services/api';
import svg from '../../static/img/account-management.svg';
import Title from '../../components/Title';
import Status from '../../components/Status';
import Button from '../../components/Button';
import { Loading } from '../../components/Loading';

const RollbackEmailContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  font-size: 16px;
  @media (max-width: 650px) {
    width: 100%;
  }
  p + p {
    margin-top: 16px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  button {
    padding: 16px;

    @media (max-width: 650px) {
      width: 100%;
    }
  }
`;

interface Params {
  otp: string;
  nameUser: string;
  newerEmail: string;
  olderEmail: string;
  date: string;
  time: string;
  ip: string;
}

const RollbackEmailPage = () => {
  const [statusRequest, setStatusRequest] = useState<string>('');
  const [params, setParams] = useState<Params>({
    otp: '',
    nameUser: '',
    newerEmail: '',
    olderEmail: '',
    date: '',
    time: '',
    ip: '',
  });

  const getParam = (param: string) => {
    const paramsURL = new URLSearchParams(window.location.hash.substring(17));
    return paramsURL.get(param) as string;
  };

  useEffect(() => {
    const dateTime = getParam('datetime');
    const formattedDate = moment(parseInt(dateTime, 10) * 1000).format(
      'DD/MM/YYYY',
    );
    const formattedTime = moment(parseInt(dateTime, 10) * 1000).format('hh:mm');

    setParams({
      otp: getParam('otp'),
      nameUser: getParam('name'),
      newerEmail: getParam('newer'),
      olderEmail: getParam('older'),
      date: formattedDate,
      time: formattedTime,
      ip: getParam('ip'),
    });
  }, []);

  const rollbackEmailRequest = (otp: string) => {
    const body = {
      otp,
    };
    setStatusRequest('wait');
    api
      .put('/email/rollback', body, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then(() => {
        setStatusRequest('success');
      })
      .catch((error: AxiosError) => {
        switch (error.response?.status) {
          case 406:
            setStatusRequest('retry');
            break;
          case 409:
            setStatusRequest('alreadyExists');
            break;
          default:
            setStatusRequest('error');
            break;
        }
      });
  };

  const InitialContent: React.FC = () => {
    return (
      <div>
        <Title variant="page">Alteração de e-mail</Title>
        <p>
          Olá, <b>{params.nameUser}</b>.
        </p>
        <p>Não solicitou a alteração de e-mail?</p>
        <p>
          Alguém tentou alterar seu e-mail usando o e-mail{' '}
          <b>{params.newerEmail}</b> se não foi você, é possível cancelar essa
          solicitação.
        </p>
        <p style={{ marginBottom: 32 }}>
          <i>Solicitação feita em</i>{' '}
          <b>
            {params.date} às {params.time}
          </b>
          <br />
          <i>Endereço de IP: </i>
          <b>{params.ip}</b>
        </p>
        <ButtonWrapper>
          <Button
            variant="primary"
            onClick={() => rollbackEmailRequest(params.otp)}
          >
            Bloquear Alteração de E-mail
          </Button>
        </ButtonWrapper>
      </div>
    );
  };

  const Success: React.FC = () => {
    return (
      <Status
        icon={`${svg}#icon-check`}
        title="Bloqueio realizado com sucesso!"
        colorTitle="#0669de"
      >
        <p>
          Sua conta voltou a ter o e-mail <b>{params.olderEmail}</b>
        </p>
        <p style={{ marginBottom: 32 }}>
          Recomendamos que você altere agora mesmo sua senha.
        </p>
        <ButtonWrapper>
          <Button
            variant="primary"
            onClick={() => {
              window.location.href = '/?redirect=alterar-senha';
            }}
          >
            Alterar Senha
          </Button>
        </ButtonWrapper>
      </Status>
    );
  };

  const Retry: React.FC = () => {
    return (
      <Status
        icon={`${svg}#icon-check-failed`}
        title="Por favor tente novamente!"
        colorTitle="#FE0000"
      />
    );
  };

  const AlreadyExists: React.FC = () => {
    return (
      <Status
        icon={`${svg}#icon-check-failed`}
        title="E-mail já registrado, por favor tente um novo e-mail!"
        colorTitle="#FE0000"
      />
    );
  };

  const Error: React.FC = () => {
    return (
      <Status
        icon={`${svg}#icon-check-failed`}
        colorTitle="#FE0000"
        title="Falha na ativação do email!"
      />
    );
  };

  const renderWithStatusResponse = () => {
    switch (statusRequest) {
      case 'success':
        return <Success />;
      case 'retry':
        return <Retry />;
      case 'alreadyExists':
        return <AlreadyExists />;
      case 'error':
        return <Error />;
      default:
        return <InitialContent />;
    }
  };

  if (statusRequest === 'wait') {
    return <Loading />;
  }

  return (
    <>
      <Header />
      <Container>
        <RollbackEmailContainer>
          {renderWithStatusResponse()}
        </RollbackEmailContainer>
      </Container>
    </>
  );
};

export default RollbackEmailPage;
